<template>
  <div class="container">
      <LoadingSpinner :loading="loading"> </LoadingSpinner>

      <div v-if="!loading" class="mt-4 mb-4 refund-page">
        <h3>Refusjon for ordre betalt med kontant og kort</h3>
        <div class="mt-4">{{getOrderTypeText()}} - Ordre #{{order.orderId}}</div>

        <!-- Step 1: Payment Method Selection -->
        <div v-if="step == 1">
          <div class="mt-4">
            <div v-if="hasMixedPayments()" class="alert alert-info">
              ℹ️ <strong>Betaling med kontant og kort:</strong> For ordre betalt med kontant og kort må hele ordren refunderes. Alle produkter og betalingsmidler er automatisk valgt.
            </div>
          </div>
          
          <div class="payment-selection mt-3">
            <div v-for="payment in order.payments" :key="payment.id" class="payment-item">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <input type="checkbox" :name="payment.id" :value="payment.id" 
                         :checked="selectedPaymentsContains(payment)" 
                         :disabled="hasMixedPayments()"
                         class="checkbox" />
                  <div>
                    <div><strong>{{ getPaymentTypeDisplayName(payment.paymentType) }}</strong></div>
                    <div class="text-muted small">{{ payment.transactionId || 'Kontant' }}</div>
                    <div v-if="hasMixedPayments()" class="text-success small">✓ Automatisk valgt</div>
                  </div>
                </div>
                <div>
                  <strong>{{ displayTwoDecimals(payment.amount) }}</strong>
                </div>
              </div>
            </div>
          </div>

          <div v-if="hasMixedPayments()" class="mt-3">
            <div class="alert alert-secondary">
              <div class="text-muted">Kort refunderes først, deretter kontant. Hele ordren refunderes.</div>
            </div>
          </div>

          <div class="bo_savebtn_container mt-5 mb-5">
            <div class="d-flex justify-content-end">
              <button type="button" class="bo_btn" @click="step=2" :disabled="selectedPayments.length === 0">Neste</button>
            </div>
          </div>
        </div>

        <!-- Step 2: Final Confirmation -->
        <div v-if="step == 2" class="mt-4">
          <hr class="mb-0"/>
          <div v-for="selectedCartItem in selectedCartItems" :key="selectedCartItem.id" :id="selectedCartItem.id" class="refundCartItem">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div>
                  <div>
                    -1 x {{selectedCartItem.itemName}}
                  </div>
                  <div v-if="selectedCartItem.itemSubselection">
                    {{ selectedCartItem.itemSubselection.name }}
                  </div>
                  <div v-if="selectedCartItem.spicyLevel != ''">
                    {{ selectedCartItem.spicyLevel}}
                  </div>
                  <div v-if="selectedCartItem.comment != ''">
                    {{ selectedCartItem.comment }}
                  </div>
                </div>
              </div>
              <div>
                -{{ displayTwoDecimals(getPriceWithVat(selectedCartItem)) }}
              </div>
            </div>
          </div>
          <div v-if="refundedTipsAmount > 0" class="mt-2 mb-3">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div> -Tips </div>
              </div>
              <div>
                -{{ displayTwoDecimals(refundedTipsAmount) }}
              </div>
            </div>
          </div>
          <div v-if="order.manualDiscount && order.manualDiscount > 0" class="mt-2 mb-3">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <div> Rabatt </div>
              </div>
              <div>
                {{ displayTwoDecimals(order.manualDiscount) }}
              </div>
            </div>
          </div>

          <div class="d-flex align-items-center justify-content-between">
            <div>
              <span style="font-weight: bold;">Totalt</span>
            </div>
            <div>
              <span style="font-weight: bold;color: red">-{{ displayTwoDecimals(getTotalRefundAmount()) }}</span>
            </div>
          </div>

          <div class="final-summary mt-4">
            <div class="alert alert-info">
              <h6>📋 Oppsummering av refusjon:</h6>
              <div><strong>Total beløp:</strong> {{ displayTwoDecimals(getTotalRefundAmount()) }}</div>
              <div class="mt-2">
                <strong>Refunderes til:</strong>
                <div class="mt-1">
                  <div v-for="refundPayment in refundDistribution.filter(p => p.refundAmount > 0)" :key="refundPayment.id" class="payment-refund-item">
                    • {{ getPaymentTypeDisplayName(refundPayment.paymentType) }}: {{ displayTwoDecimals(refundPayment.refundAmount) }}
                  </div>
                </div>
              </div>
              <div v-if="hasCashRefund()" class="mt-2 text-warning">
                ⚠️ <strong>Kontantrefusjon:</strong> Åpner kasseskuffen automatisk
              </div>
            </div>
          </div>

          <div class="bo_savebtn_container mt-5 mb-5">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <button type="button" class="bo_btn" @click="step=1">Tilbake</button>
              </div>
              <div>
                <button type="button" class="bo_btn" @click="executeRefund">Refunder</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import OrderService from "@/services/order/OrderService";
import OrderUtil from "@/util/OrderUtil";
import VatUtil from "@/util/VatUtil";
import ConfirmDialogue from "@/components/other/ConfirmDialogue";
import CashAndCardService from "@/services/payment/CashAndCardService";
import VivaService from "@/services/payment/VivaService";
import MathUtil from "@/util/MathUtil";
import RefundJournal from "@/util/RefundJournal";

export default {
  components: {LoadingSpinner, ConfirmDialogue},
  data() {
    return {
      loading: true,
      step: 1,

      vat: {},
      orderDocumentId: '',
      order: {
        type: "order",
        merchant: '',
        orderId: '',
        orderType: '',
        orderStatus: '',
        totalAmount: 0,
        description: '',
        pickupOrDeliveryTime: '',
        mobileNumber: '',
        name: '',
        nameOnDoorbell: '',
        street: '',
        houseNumber: '',
        floor: '',
        zipCode: '',
        city: '',
        orderedTime: '',
        modifiedTime: '',
        servedBy: '',
        channel: '',
        payments: [],
        vats: [],
        totalVat: 0,
        cartItemsJson: [],
        manualDiscount: 0
      },
      refundCartItems: [],
      refundedTipsAmount : 0,
      selectedCartItems: [],
      refundedPayments: [], // Track refunded payments data

      // Data for mixed payment refund
      selectedPayments: [],
      refundDistribution: [],
      refundExecutionStatus: {
        cash: { executed: false, success: false },
        card: { executed: false, success: false }
      },

      // Block back button during cash refund
      cashRefundInProgress: false
    };
  },
  mounted() {
    document.getElementById('nav').hidden = true;
    document.getElementById('footer').hidden = true;
    document.getElementById('scrollTopBtn').hidden = true;

    // Override back button functionality
    window.onBackPressed = () => {
      if(this.cashRefundInProgress) {
        // Block back button during cash refund
        console.log('Back button blocked during cash refund');
        return;
      }
      // Allow normal back navigation - go back to previous page
      this.$router.go(-1);
    };

    this.orderDocumentId = this.$route.query.orderDocumentId;
    OrderService.getDataForOrderDetailGui(this.orderDocumentId).then(
        (response) => {
          let dataForGui = response.data;
          //console.log('dataForGui: ' + JSON.stringify(dataForGui));
          this.order = dataForGui.order;
          this.vat = dataForGui.vat;
          this.refundCartItems = this.getRefundCartItems();
          
          // Auto-select all payments initially
          if(this.order.payments && this.order.payments.length > 0){
            this.selectedPayments = [...this.order.payments];
          }
          
          // For mixed payments, auto-select all items and tips
          if(this.hasMixedPayments()){
            this.selectedCartItems = [...this.refundCartItems];
            this.refundedTipsAmount = this.order.tipsAmount || 0;
            this.calculateRefundDistribution();
          }
          
          this.loading = false;
        },
        (error) => {
          console.log(error);
        }
    );
  },
  methods:{
    getRefundCartItems(){
      let refundCartItems = [];
      let cartItems = this.order.cartItems;
      console.log('cartItems: ' + JSON.stringify(cartItems));
      for (let i = 0; i < cartItems.length; i++) {
        let cartItem = cartItems[i];
        for(let x = 1; x <= cartItem.count; x++){
          let refundCartItem = {
            originalCartItemId : cartItem.id,
            id: cartItem.id + '_' + x, //make it unique
            itemId: cartItem.itemId,
            itemName: cartItem.itemName + OrderUtil.shareSuffix(cartItem),
            shareAmount: cartItem.shareAmount,
            shareNumerator: cartItem.shareNumerator,
            shareDenominator: cartItem.shareDenominator,
            shareOfCount: cartItem.shareOfCount,
            itemType: cartItem.itemType,
            price: cartItem.price,
            discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0,
            count: 1,
            itemSubselection: cartItem.itemSubselection,
            spicyLevel: cartItem.spicyLevel,
            comment: cartItem.comment,
            createdDate: cartItem.createdDate
          };
          refundCartItems.push(refundCartItem);

          //subselection addOns
          if(refundCartItem.itemSubselection){
            let subselectionAddOnCartItems =  refundCartItem.itemSubselection.addOnCartItems;
            if(subselectionAddOnCartItems){
              for(let a = 0; a < subselectionAddOnCartItems.length; a++){
                let addOnCartItem = subselectionAddOnCartItems[a];
                let addOn = addOnCartItem.addOn;
                for(let y = 1; y <= addOnCartItem.count; y++){
                  //Only if it isn't free
                  if(addOnCartItem.price > 0){
                    let addOnRefundCartItem = {
                      originalCartItemId : cartItem.id, //The id of the parent cartItem the addon belongs to.
                      id: cartItem.id + '_' + x + '_' + addOnCartItem.addOnId + '_' + y, //make it unique
                      itemId: addOnCartItem.addOnId,
                      itemName: addOn.name,
                      itemType: 'ADD_ON_OF_ADD_ON_GROUP',
                      price: addOnCartItem.price,
                      discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0, //The same as cartItem.discountPercent
                      count: 1,
                      createdDate: cartItem.createdDate
                    };
                    refundCartItems.push(addOnRefundCartItem);
                  }
                }
              }
            }
          }

          //item addOns
          if(cartItem.addOnCartItems && cartItem.addOnCartItems.length > 0){
            for (let a = 0; a < cartItem.addOnCartItems.length; a++) {
              let addOnCartItem = cartItem.addOnCartItems[a];
              let addOn = addOnCartItem.addOn;
              for(let y = 1; y <= addOnCartItem.count; y++){
                //Only if it isn't free
                if(addOnCartItem.price > 0){
                  let addOnRefundCartItem = {
                    originalCartItemId : cartItem.id, //The id of the parent cartItem the addon belongs to.
                    id: cartItem.id + '_' + x + '_' + addOnCartItem.addOnId + '_' + y, //make it unique
                    itemId: addOnCartItem.addOnId,
                    itemName: addOn.name,
                    itemType: 'ADD_ON_OF_ADD_ON_GROUP',
                    price: addOnCartItem.price,
                    discountPercent: cartItem.discountPercent ? cartItem.discountPercent : 0, //The same as cartItem.discountPercent
                    count: 1,
                    createdDate: cartItem.createdDate
                  };
                  refundCartItems.push(addOnRefundCartItem);
                }
              }
            }
          }
        }
      }
      return refundCartItems;
    },

    getOrderTypeText(){
      return OrderUtil.getOrderTypeText(this.order.orderType).toUpperCase();
    },
    getPriceWithVat(cartItem){
      if(OrderUtil.isShare(cartItem) && !(this.order.manualDiscount > 0)){
        return OrderUtil.shareAmount(cartItem); //a share as the till worked it out, never rounded to whole kroner
      }
      let vatRate = VatUtil.getVatRate(this.vat, this.order.orderType, cartItem.itemType);
      let priceWithVat = OrderUtil.getPriceWithVat(vatRate, cartItem.price) * (100 - cartItem.discountPercent) / 100;
      if(cartItem.discountPercent == 0){
        return Math.round(priceWithVat); //make it exact as it is in order description.
      } else {
        return MathUtil.roundTwoDecimals(priceWithVat);
      }
    },
    getTotalRefundAmount(){
      // For mixed payments, return the total amount from payments array
      // This includes manualDiscount and other order-level adjustments
      if(this.hasMixedPayments() && this.order.payments && this.order.payments.length > 0){
        return this.order.payments.reduce((total, payment) => total + payment.amount, 0);
      }

      let totalPricesInCart = 0.00;
      for (let i = 0; i < this.selectedCartItems.length; i++) {
        let selectedCartItem = this.selectedCartItems[i];
        let cartItemPrice = this.getPriceWithVat(selectedCartItem);
        console.log(cartItemPrice);
        totalPricesInCart = totalPricesInCart + cartItemPrice;
      }
      totalPricesInCart = totalPricesInCart + this.refundedTipsAmount;
      
      // Add manual discount if present
      if(this.order.manualDiscount && this.order.manualDiscount > 0){
        totalPricesInCart = totalPricesInCart - this.order.manualDiscount;
      }
      
      return totalPricesInCart;
    },
    incrementGrandTotalRefund(refundAmount, orderDocumentId){
      if(window.JSBridge && window.JSBridge.incrementGrandTotalRefund) {
        window.JSBridge.incrementGrandTotalRefund(refundAmount, orderDocumentId);
      }
    },
    goToOrderDetailPage(){
      this.$router.push({ path: 'orderDetail', query: {
          orderDocumentId: this.orderDocumentId,
          showRefundInfo: 'false'
        }
      });
    },

    async showRefundFailedMessage(message){
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Refusjonen mislykket',
        message: message || 'Det skjedde en feil og refusjonen ble ikke fullført.',
        okButton: 'OK',
        cancelButton: 'NOT_SET'
      })
      if(ok) {
        //Do nothing
      }
    },

    displayTwoDecimals(num){
      if(num > 0 || num < 0){
        return MathUtil.displayTwoDecimals(num);
      }else {
        return num;
      }
    },

    hasMixedPayments(){
      if(!this.order.payments || this.order.payments.length <= 1) return false;
      const paymentTypes = this.order.payments.map(p => p.paymentType);
      return new Set(paymentTypes).size > 1;
    },

    getPaymentTypeDisplayName(paymentType){
      if(paymentType === 'CASH') return 'Kontant';
      if(paymentType === 'DEBIT_CARD' || paymentType === 'CREDIT_CARD') return 'Kort';
      if(paymentType === 'MOBILE_APP') return 'Vipps';
      if(paymentType.includes('vipps')) return 'Vipps';
      if(paymentType.includes('creditcard')) return 'Kort';
      if(paymentType === 'payex.applepay') return 'Apple Pay';
      return paymentType;
    },

    selectedPaymentsContains(payment){
      return this.selectedPayments.some(p => p.id === payment.id);
    },

    calculateRefundDistribution(){
      if(this.selectedPayments.length === 0) return;
      
      const totalRefund = this.getTotalRefundAmount();
      this.refundDistribution = [];
      
      // Sort payments by preference: card first, then cash
      const sortedPayments = [...this.selectedPayments].sort((a, b) => {
        const aScore = this.getPaymentPriority(a.paymentType);
        const bScore = this.getPaymentPriority(b.paymentType);
        return bScore - aScore; // Higher score first (card > cash)
      });

      this.calculateAutoDistribution(sortedPayments, totalRefund);
    },

    getPaymentPriority(paymentType){
      if(paymentType === 'CASH') return 1;
      return 2; // All card types get higher priority
    },

    calculateAutoDistribution(sortedPayments, totalRefund){
      let remainingRefund = totalRefund;
      
      for(let payment of sortedPayments){
        const refundAmount = Math.min(remainingRefund, payment.amount);
        this.refundDistribution.push({
          id: payment.id,
          paymentType: payment.paymentType,
          originalAmount: payment.amount,
          refundAmount: refundAmount,
          transactionId: payment.transactionId
        });
        remainingRefund -= refundAmount;
        
        if(remainingRefund <= 0) break;
      }
    },

    hasCashRefund(){
      return this.refundDistribution.some(p => p.paymentType === 'CASH' && p.refundAmount > 0);
    },

    async executeRefund(){
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Refundere kr ' + this.displayTwoDecimals(this.getTotalRefundAmount()),
        message: 'Vil du refundere kr ' + this.displayTwoDecimals(this.getTotalRefundAmount()) + ' til kunden?',
        okButton: 'Refunder',
        cancelButton: 'Avbryt',
        spreadButtons: true,
      });
      
      if(!ok) return;
      
      this.loading = true;
      try {
        await this.executeSequentialRefunds();
        this.goToOrderDetailPage();
      } catch (error) {
        console.error('Refund execution failed:', error);
        this.loading = false;
        await this.showRefundFailedMessage(error && error.refundFailedMessage);
      }
    },

    async executeSequentialRefunds(){
      // Execute refunds in order: card first, then cash
      const cardRefunds = this.refundDistribution.filter(p => p.paymentType !== 'CASH' && p.refundAmount > 0);
      const cashRefunds = this.refundDistribution.filter(p => p.paymentType === 'CASH' && p.refundAmount > 0);

      // One refund, however many ways it is paid back, is one return: the till books what was paid back on its grand
      // total and journals one return receipt for it (JSBridge.incrementGrandTotalRefund), even if a later leg fails.
      // Each leg used to call it, which journaled a «Kontant og kort» refund as two returns.
      let refunded = 0;
      const paidBack = [];
      try {
        // Execute card refunds first (Viva API - no card tap needed)
        for(let refund of cardRefunds){
          await this.executeVivaCardRefund(refund);
          refunded += refund.refundAmount;
          paidBack.push(refund);
        }

        // Execute cash refunds last
        for(let refund of cashRefunds){
          await this.executeCashRefund(refund);
          refunded += refund.refundAmount;
          paidBack.push(refund);
        }
      } finally {
        if(refunded > 0){
          this.incrementGrandTotalRefund(MathUtil.roundTwoDecimals(refunded), this.orderDocumentId);
          this.journalRefund(paidBack);
        }
      }
    },

    /** The return in the till's journal, with the legs that went through. */
    journalRefund(paidBack){
      RefundJournal.send({
        order: this.order,
        orderDocumentId: this.orderDocumentId,
        rows: this.selectedCartItems,
        priceOf: row => this.getPriceWithVat(row),
        linesTotal: this.getRefundedLinesAmount(),
        paidBack: paidBack.map(leg => ({ paymentType: leg.paymentType, amount: leg.refundAmount, transactionId: leg.transactionId })),
        tips: this.refundedTipsAmount,
        planned: this.getTotalRefundAmount(),
      });
    },

    /**
     * What the refunded lines came to after every discount. A sale paid in cash and by card is refunded whole, at what
     * was paid: its lines are then what the till sold them at (toPayAmount, less tips), and the cash rounding of the
     * sale comes back with it.
     */
    getRefundedLinesAmount(){
      if(this.hasMixedPayments() && this.order.toPayAmount > 0){
        return this.order.toPayAmount - (this.order.tipsAmount || 0);
      }
      return this.getTotalRefundAmount() - this.refundedTipsAmount;
    },

    async executeVivaCardRefund(refund){
      // Viva refunds are processed via backend API - no card tap needed
      const transactionId = refund.transactionId;

      if(!transactionId){
        throw new Error('No transactionId found for Viva refund');
      }

      // Create refunded cart items with VAT
      const refundedCartItemsWithVat = this.selectedCartItems.map(cartItem => {
        if (OrderUtil.isShare(cartItem)) {
          // A share at its own amount, as the till sold it: its price rounded to whole kroner refunded a third
          // of a 499 bottle as 166 and left the VAT and the category short.
          return { ...cartItem, price: OrderUtil.shareAmount(cartItem) };
        }
        let vatRate = VatUtil.getVatRate(this.vat, this.order.orderType, cartItem.itemType);
        let priceWithVat = OrderUtil.getPriceWithVat(vatRate, cartItem.price) * (100 - cartItem.discountPercent) / 100;
        let finalPrice = cartItem.discountPercent === 0 ? Math.round(priceWithVat) : MathUtil.roundTwoDecimals(priceWithVat);
        return {
          ...cartItem,
          price: finalPrice
        };
      });

      const response = await VivaService.refund(transactionId, refund.refundAmount, refundedCartItemsWithVat, this.refundedTipsAmount, this.orderDocumentId);
      if(response.data === 'SUCCEEDED'){
        this.refundExecutionStatus.card.executed = true;
        this.refundExecutionStatus.card.success = true;
      } else {
        this.refundExecutionStatus.card.executed = true;
        this.refundExecutionStatus.card.success = false;
        const error = new Error('Viva refund failed: ' + response.data);
        error.refundFailedMessage = VivaService.refundFailedMessage(response.data, refund.refundAmount);
        throw error;
      }
    },

    async executeCashRefund(refund){
      // Stop loading spinner so dialog can be shown
      this.loading = false;

      // Open cash drawer first
      this.openCashDrawer();
      
      // Block back button and hide navigation during cash refund
      this.cashRefundInProgress = true;
      this.setBackButtonBlocked(true);
      this.setNavigationDisabled(true);
      
      // Show confirmation dialog for cash payout
      await this.$refs.confirmDialogue.show({
        title: 'Betal tilbake kontant NOK ' + this.displayTwoDecimals(refund.refundAmount),
        message: 'Betal tilbake kontant NOK ' + this.displayTwoDecimals(refund.refundAmount) + ' til kunden. Trykk deretter på Bekreft for å fullføre refusjonen.',
        okButton: 'Bekreft',
        cancelButton: 'NOT_SET',
      });

      // Show loading again while recording the refund
      this.loading = true;
      
      // Create a copy of selectedCartItems with base prices (without manual discount applied)
      // This follows the same pattern as PayMyPartOrderRefund.vue
      const refundedCartItemsWithVat = this.selectedCartItems.map(cartItem => {
        if (OrderUtil.isShare(cartItem)) {
          // A share at its own amount, as the till sold it: its price rounded to whole kroner refunded a third
          // of a 499 bottle as 166 and left the VAT and the category short.
          return { ...cartItem, price: OrderUtil.shareAmount(cartItem) };
        }
        // Calculate VAT-inclusive price based on the original base price (without manual discount)
        let vatRate = VatUtil.getVatRate(this.vat, this.order.orderType, cartItem.itemType);
        let priceWithVat = OrderUtil.getPriceWithVat(vatRate, cartItem.price) * (100 - cartItem.discountPercent) / 100;
        let finalPrice = cartItem.discountPercent === 0 ? Math.round(priceWithVat) : MathUtil.roundTwoDecimals(priceWithVat);
        
        return {
          ...cartItem,
          price: finalPrice
        };
      });

      // Record refund data using CashAndCardService
      return new Promise((resolve, reject) => {
        CashAndCardService.refund(
          this.getTotalRefundAmount(),
          this.refundedPayments,
          refundedCartItemsWithVat,
          this.refundedTipsAmount,
          this.orderDocumentId
        ).then(
          (response) => {
            if(response.data === 'SUCCEEDED'){
              this.refundExecutionStatus.cash.executed = true;
              this.refundExecutionStatus.cash.success = true;
              this.cashRefundInProgress = false;
              this.setBackButtonBlocked(false);
              this.setNavigationDisabled(false);
              resolve();
            } else {
              this.refundExecutionStatus.cash.executed = true;
              this.refundExecutionStatus.cash.success = false;
              this.cashRefundInProgress = false;
              this.setBackButtonBlocked(false);
              this.setNavigationDisabled(false);
              reject(new Error('Cash refund failed'));
            }
          },
          (error) => {
            this.refundExecutionStatus.cash.executed = true;
            this.refundExecutionStatus.cash.success = false;
            this.cashRefundInProgress = false;
            this.setBackButtonBlocked(false);
            this.setNavigationDisabled(false);
            reject(error);
          }
        );
      });
    },

    openCashDrawer(){
      if(window.JSBridge && window.JSBridge.openCashDrawer) {
        window.JSBridge.openCashDrawer();
      }
    },

    setBackButtonBlocked(blocked){
      if(window.JSBridge && window.JSBridge.setBackButtonBlocked) {
        window.JSBridge.setBackButtonBlocked(blocked);
      }
    },

    setNavigationDisabled(disabled){
      if(window.JSBridge && window.JSBridge.setNavigationDisabled) {
        window.JSBridge.setNavigationDisabled(disabled);
      }
    }
  }
}
</script>

<style scoped>
  .refundCartItem{
    padding-top: 10px;
    padding-bottom: 10px;
    border-bottom: var(--bo-muted-2);
    border-bottom: 1px  solid var(--bo-muted-2);
  }

  .checkbox{
    width: 25px;
    height: 25px;
    margin-right: 10px;
  }

  /* New styles for multi-payment refund */
  .payment-item {
    padding: 12px;
    border: 1px solid var(--bo-line);
    border-radius: 8px;
    margin-bottom: 8px;
    background-color: var(--bo-surface-2);
  }

  .payment-item:hover {
    background-color: var(--bo-line);
  }


  .final-summary .alert {
    border-left: 4px solid #17a2b8;
  }


  .payment-refund-item {
    display: block;
    margin-bottom: 6px;
    margin-left: 15px;
    line-height: 1.4;
  }

  /* Disabled state for mixed payments */
  .payment-item input:disabled,
  .refundCartItem input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .payment-item:has(input:disabled) {
    background-color: var(--bo-surface-2);
    opacity: 0.9;
  }

  .refundCartItem:has(input:disabled) {
    background-color: var(--bo-surface-2);
    opacity: 0.9;
  }
</style>
